import React, { memo, useEffect } from "react";

import { Drawer, DrawerOverlay } from "@chakra-ui/react";
import UserProfileDrawer from "modules/user-profile/user-profile-drawer";
import ComposeChatDrawer from "modules/drawers/compose-chat-drawer";
import ComposeThreadDrawer from "modules/drawers/compose-thread-drawer";
import DownloadsExportsDrawer from "modules/drawers/downloads-exports-drawer";
import MessageThreadFilesDrawer from "modules/messaging/drawers/files-drawer/message-thread-files-drawer";
import MessageThreadProfileDrawer from "modules/drawers/message-thread-profile-drawer";
import SupportDrawer from "modules/drawers/support-drawer";
import AdminDistributionGroupDrawer from "modules/admin/admin-distribution-group-drawer";
import AdminTagCategoryDrawer from "modules/admin/admin-tag-category-drawer";
import AdminTagDrawer from "modules/admin/admin-tag-drawer";
import AdminUserDrawer from "modules/admin/admin-user-drawer";
import DistributionGroupProfileDrawer from "modules/drawers/distribution-group-profile-drawer";
import FiltersDrawer from "modules/messaging/search/filters-drawer";
import RelatedMessageThreadsDrawer from "modules/drawers/related-message-threads-drawer";
import ForwardMessageThreadDrawer from "modules/drawers/forward-message-thread-drawer";
import GenerateTaskFromMessagesDrawer from "modules/drawers/generate-task-from-messages-drawer";
import { DRAWER_TYPE, useOverlay } from "./overlay-provider";
import AdminAddEditDistributionGroupDrawer from "modules/admin/admin-add-edit-distribution-group-drawer";
import AdminAddUserDrawer from "modules/admin/admin-add-user-drawer";
import AdminUserAttributeCategoryDrawer from "modules/admin/admin-user-attribute-category-drawer";
import ComposeTaskDrawer from "modules/messaging/tasks/compose-task-drawer";
import MessageProfileDrawer from "modules/messaging/messages/message-profile-drawer";
import InternalAdminAddEditDistributionGroupDrawer from "modules/internal-admin/drawers/internal-admin-add-edit-distribution-group-drawer";
import InternalAdminAddUserDrawer from "modules/internal-admin/drawers/internal-admin-add-user-drawer";
import InternalAdminDistributionGroupDrawer from "modules/internal-admin/drawers/internal-admin-distribution-group-drawer";
import InternalAdminTagCategoryDrawer from "modules/internal-admin/drawers/internal-admin-tag-category-drawer";
import InternalAdminTagDrawer from "modules/internal-admin/drawers/internal-admin-tag-drawer";
import InternalAdminUserAttributeCategoryDrawer from "modules/internal-admin/drawers/internal-admin-user-attribute-category-drawer";
import InternalAdminUserDrawer from "modules/internal-admin/drawers/internal-admin-user-drawer";
import ComposeHappycoThreadDrawer from "modules/external/happyco/compose-happyco-thread-drawer";
import HappycoCreateWorkOrderDrawer from "modules/external/happyco/happyco-create-work-order-drawer";
import HappycoExportToExistingWorkOrderDrawer from "modules/external/happyco/happyco-export-to-existing-work-order-drawer";
import AddUsersToVideoCallDrawer from "modules/drawers/add-users-to-video-call-drawer";
import VideoCallParticipantsDrawer from "modules/drawers/video-call-participants-drawer";

const Drawers = () => {
  const { drawerState, closeDrawer } = useOverlay();
  const [component, setComponent] = React.useState<React.ReactNode>();

  useEffect(() => {
    if (!drawerState) {
      setComponent(undefined);
      return;
    }
    switch (drawerState.type) {
      case DRAWER_TYPE.COMPOSE_THREAD:
        setComponent(<ComposeThreadDrawer />);
        break;
      case DRAWER_TYPE.COMPOSE_HAPPYCO_THREAD:
        setComponent(<ComposeHappycoThreadDrawer />);
        break;
      case DRAWER_TYPE.COMPOSE_CHAT:
        setComponent(<ComposeChatDrawer />);
        break;
      case DRAWER_TYPE.COMPOSE_TASK:
        setComponent(<ComposeTaskDrawer />);
        break;
      case DRAWER_TYPE.USER_PROFILE:
        if (!drawerState.id) break;
        setComponent(<UserProfileDrawer userId={drawerState.id} />);
        break;
      case DRAWER_TYPE.DISTRIBUTION_GROUP_PROFILE:
        if (!drawerState.id) break;
        setComponent(
          <DistributionGroupProfileDrawer
            distributionGroupId={drawerState.id}
          />,
        );
        break;
      case DRAWER_TYPE.MESSAGE_THREAD_FILES:
        if (!drawerState.id) break;
        setComponent(
          <MessageThreadFilesDrawer messageThreadId={drawerState.id} />,
        );
        break;
      case DRAWER_TYPE.MESSAGE_PROFILE:
        if (!drawerState.id) break;
        if (!drawerState.id2) break;
        setComponent(
          <MessageProfileDrawer
            messageId={drawerState.id}
            messageThreadId={drawerState.id2}
          />,
        );
        break;
      case DRAWER_TYPE.MESSAGE_THREAD_PROFILE:
        if (!drawerState.id) break;
        setComponent(
          <MessageThreadProfileDrawer messageThreadId={drawerState.id} />,
        );
        break;
      case DRAWER_TYPE.RElATED_MESSAGE_THREADS:
        if (!drawerState.id) break;
        setComponent(
          <RelatedMessageThreadsDrawer messageThreadId={drawerState.id} />,
        );
        break;
      case DRAWER_TYPE.SUPPORT:
        setComponent(<SupportDrawer />);
        break;
      case DRAWER_TYPE.SEARCH_FILTERS:
        setComponent(<FiltersDrawer type={drawerState.messageThreadType} />);
        break;
      case DRAWER_TYPE.DOWNLOADS_EXPORTS:
        setComponent(<DownloadsExportsDrawer />);
        break;

      case DRAWER_TYPE.HAPPYCO_CREATE_WORK_ORDER:
        setComponent(<HappycoCreateWorkOrderDrawer />);
        break;
      case DRAWER_TYPE.HAPPYCO_EXPORT_TO_EXISTING_WORK_ORDER:
        setComponent(<HappycoExportToExistingWorkOrderDrawer />);
        break;

      case DRAWER_TYPE.ADMIN_USER:
        if (!drawerState.id) break;
        setComponent(<AdminUserDrawer userId={drawerState.id} />);
        break;
      case DRAWER_TYPE.ADMIN_ADD_USER:
        setComponent(<AdminAddUserDrawer />);
        break;
      case DRAWER_TYPE.ADMIN_DISTRIBUTION_GROUP:
        if (!drawerState.id) break;
        setComponent(
          <AdminDistributionGroupDrawer distributionGroupId={drawerState.id} />,
        );
        break;
      case DRAWER_TYPE.ADMIN_ADD_EDIT_DISTRIBUTION_GROUP:
        setComponent(
          <AdminAddEditDistributionGroupDrawer
            distributionGroupId={drawerState.id}
          />,
        );
        break;
      case DRAWER_TYPE.ADMIN_TAG:
        if (!drawerState.id) break;
        setComponent(<AdminTagDrawer tagId={drawerState.id} />);
        break;
      case DRAWER_TYPE.ADMIN_TAG_CATEGORY:
        if (!drawerState.id) break;
        setComponent(<AdminTagCategoryDrawer tagCategoryId={drawerState.id} />);
        break;
      case DRAWER_TYPE.ADMIN_USER_ATTRIBUTE_CATEGORY:
        if (!drawerState.id) break;
        setComponent(
          <AdminUserAttributeCategoryDrawer
            userAttributeCategoryId={drawerState.id}
          />,
        );
        break;

      case DRAWER_TYPE.INTERNAL_ADMIN_USER:
        if (!drawerState.id) break;
        setComponent(<InternalAdminUserDrawer userId={drawerState.id} />);
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_ADD_USER:
        setComponent(<InternalAdminAddUserDrawer />);
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_DISTRIBUTION_GROUP:
        if (!drawerState.id) break;
        setComponent(
          <InternalAdminDistributionGroupDrawer
            distributionGroupId={drawerState.id}
          />,
        );
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_ADD_EDIT_DISTRIBUTION_GROUP:
        setComponent(
          <InternalAdminAddEditDistributionGroupDrawer
            distributionGroupId={drawerState.id}
          />,
        );
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_USER_ATTRIBUTE_CATEGORY:
        if (!drawerState.id) break;
        setComponent(
          <InternalAdminUserAttributeCategoryDrawer
            userAttributeCategoryId={drawerState.id}
          />,
        );
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_TAG:
        if (!drawerState.id) break;
        setComponent(<InternalAdminTagDrawer tagId={drawerState.id} />);
        break;
      case DRAWER_TYPE.INTERNAL_ADMIN_TAG_CATEGORY:
        if (!drawerState.id) break;
        setComponent(
          <InternalAdminTagCategoryDrawer tagCategoryId={drawerState.id} />,
        );
        break;
      case DRAWER_TYPE.FORWARD_MESSAGE_THREAD:
        setComponent(
          <ForwardMessageThreadDrawer
            initialForwardToState={drawerState.forwardToState}
          />,
        );
        break;
      case DRAWER_TYPE.GENERATE_TASK_FROM_MESSAGES:
        setComponent(<GenerateTaskFromMessagesDrawer />);
        break;
      case DRAWER_TYPE.VIDEO_CALL_ADD_PARTICIPANTS: {
        if (!drawerState.videoCallId || !drawerState.videoCallAccessUserIds) break;
        setComponent(
          <AddUsersToVideoCallDrawer
            callId={drawerState.videoCallId}
            accessUserIdList={drawerState.videoCallAccessUserIds}
          />,
        );
        break;
      }
      case DRAWER_TYPE.VIDEO_CALL_PARTICIPANTS: {
        if (!drawerState.videoCallAccessUserIds) break;
        setComponent(
          <VideoCallParticipantsDrawer
            accessUserIdList={drawerState.videoCallAccessUserIds}
            activeUserIdList={drawerState.videoCallActiveUserIds ?? []}
          />,
        );
        break;
      }
      default:
        setComponent(undefined);
        break;
    }
  }, [closeDrawer, drawerState]);

  return (
    <Drawer
      isOpen={component !== undefined}
      placement="right"
      onClose={closeDrawer}
      variant="synco"
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      {component}
    </Drawer>
  );
};

export default memo(Drawers);
